import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setCurrentChannel } from '../../store/slices/playerSlice';
import { setError, clearErrorTimeout, setErrorTimeout, removeChannel, setLoading, clearChannels, appendChannels, toggleChannelSelection, clearSelectedChannels, setChannels } from '../../store/slices/playlistSlice';
import { toggleFavorite } from '../../store/slices/preferencesSlice';
import { Plus, Folder, Tv, X, Search, Trash2, Pin, Download, Loader2, Edit, Check, Save, Share2, Upload, AlertCircle, Lock, ExternalLink, Square, CheckSquare } from 'lucide-react';
import { PlaylistImport } from '../PlaylistImport/PlaylistImport';
import { LinkInput } from '../PlaylistImport/LinkInput';
import { generateM3U8, parseM3U8 } from '../../utils/m3u8Parser';
import type { Channel } from '../../types/index';
import { useFirestore } from '../../hooks/useFirestore';
import { ShareButton } from '../ui/ShareButton';
import { ExtensionNotification } from '../ui/ExtensionNotification';
import { ShareModal } from '../Share/ShareModal';
import { shareService } from '../../services/shareService';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import { usePlaylistAutoSave } from '../../hooks/usePlaylistAutoSave';
import { saveChannelsLocally } from '../../utils/localStorageUtils';
import { handleHttpStream, getExtensionStoreUrl, getExtensionErrorMessage, isHttpStream, isEdgeBrowser } from '../../utils/extensionUtils';
import clsx from 'clsx';

export const Playlist: React.FC = () => {
  const dispatch = useDispatch();
  const { currentChannel } = useSelector((state: RootState) => state.player);
  const { favorites } = useSelector((state: RootState) => state.preferences);
  const { channels, error, notificationType, isLoading, selectedChannels } = useSelector((state: RootState) => state.playlist);
  const [activeTab, setActiveTab] = useState<'channels' | 'add'>('channels');
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useSelector((state: RootState) => state.auth);
  const { loadPlaylist, deletePlaylist, savePlaylist } = useFirestore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [preventReload, setPreventReload] = useState(false);
  const [prevUser, setPrevUser] = useState<typeof user>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [mode, setMode] = useState<'view' | 'edit' | 'share'>('view');
  const [shareUrl, setShareUrl] = useState<string>();
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const [shareError, setShareError] = useState<string>();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { savePlaylist: autoSavePlaylist } = useAutoSave(activeTab === 'add');
  const [prevChannels, setPrevChannels] = useState(channels);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [urlInput, setUrlInput] = useState('');
  const { autoSave } = usePlaylistAutoSave();
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // Function to check localStorage for active tab preference
  const checkTabPreference = useCallback(() => {
    const savedTab = localStorage.getItem('activePlaylistTab');
    if (savedTab === 'channels' || savedTab === 'add') {
      logger.debug('Setting active tab from localStorage:', savedTab);
      setActiveTab(savedTab);
      // Clear the stored value to avoid persistent overriding of user navigation
      localStorage.removeItem('activePlaylistTab');
    }
  }, []);

  // Check for active tab in localStorage on component mount
  useEffect(() => {
    checkTabPreference();
  }, [checkTabPreference]);

  // Check for active tab whenever location changes (URL parameters change)
  useEffect(() => {
    // When location changes, check if we need to update the active tab
    const queryParams = new URLSearchParams(location.search);
    const hasPlaylistParam = queryParams.has('playlist');
    
    if (hasPlaylistParam) {
      logger.debug('Playlist parameter detected, checking localStorage for tab preference');
      checkTabPreference();
    }
  }, [location, checkTabPreference]);

  // Set up automatic error dismissal
  useEffect(() => {
    if (error) {
      logger.debug('Setting up error timeout', { error, notificationType });
      dispatch(clearErrorTimeout());
      const timeout = window.setTimeout(() => {
        logger.debug('Error timeout triggered - clearing error');
        dispatch(setError({ message: null, type: null }));
      }, 4000); // Increased from 2000ms to 4000ms for better readability
      dispatch(setErrorTimeout(timeout));
      return () => {
        logger.debug('Cleaning up error timeout');
        dispatch(clearErrorTimeout());
      };
    }
  }, [error, dispatch]);

  // Load playlist only on login
  useEffect(() => {
    const loadSavedPlaylist = async () => {
      const hasLoaded = localStorage.getItem(`playlist_loaded_${user?.uid}`);
      // Only load from Firestore when logging in and haven't loaded before
      if (user && !prevUser && !preventReload && !hasLoaded) {
        try {
          logger.log('Starting to load playlist...');
          dispatch(setLoading(true));
          const savedChannels = await loadPlaylist(user.uid);
          if (savedChannels) {
            dispatch(appendChannels(savedChannels));
            logger.log('Merged Firestore channels with local channels');
            setActiveTab('channels'); // Switch to channels tab after loading playlist
          }
          localStorage.setItem(`playlist_loaded_${user.uid}`, 'true');
        } catch (error) {
          logger.error('Error loading playlist:', error);
          dispatch(setError({ message: 'Failed to load your playlist', type: 'error' }));
          dispatch(setLoading(false));
        } finally {
          setTimeout(() => {
            logger.log('Setting loading state to false after delay');
            dispatch(setLoading(false));
          }, 1000);
        }
      } else if (!user && prevUser) {
        // When logging out, only remove the loaded flag, but keep the channels
        // as they should now be saved in local storage by usePlaylistSync
        localStorage.removeItem(`playlist_loaded_${prevUser.uid}`);
      }
    };

    loadSavedPlaylist();
    setPrevUser(user);
  }, [user, dispatch, preventReload, prevUser]);

  // Track changes in edit mode
  useEffect(() => {
    if (mode === 'edit') {
      setPrevChannels([...channels]);
      setHasUnsavedChanges(false);
    }
  }, [mode]);

  // Track actual changes to channels
  useEffect(() => {
    if (mode === 'edit') {
      const hasChanges = JSON.stringify(channels) !== JSON.stringify(prevChannels);
      setHasUnsavedChanges(hasChanges);
    }
  }, [channels, mode, prevChannels]);

  const handleChannelSelect = (channel: Channel) => {
    dispatch(setCurrentChannel(channel));
  };

  const handleImportSuccess = () => {
    setActiveTab('channels');
    setPrevChannels([...channels]);
    setHasUnsavedChanges(true);
  };

  const handleToggleFavorite = (e: React.MouseEvent, channelId: string) => {
    e.stopPropagation();
    dispatch(toggleFavorite(channelId));
  };

  const handleDismissError = () => {
    dispatch(clearErrorTimeout());
    dispatch(setError({ message: null, type: null }));
  };

  const handleEditChannel = (e: React.MouseEvent, channel: Channel) => {
    e.stopPropagation();
    setEditingChannel(channel);
    setActiveTab('add');
  };

  // Filter channels based on search query
  const filterChannels = (channelList: Channel[]) => {
    if (!searchQuery) return channelList;
    const query = searchQuery.toLowerCase();
    return channelList.filter(channel => 
      channel.name.toLowerCase().includes(query) || 
      channel.group.toLowerCase().includes(query)
    );
  };

  // First, separate favorites from non-favorites
  const favoriteChannels = filterChannels(channels.filter(channel => favorites.includes(channel.id)));
  const nonFavoriteChannels = filterChannels(channels.filter(channel => !favorites.includes(channel.id)));

  // Group non-favorite channels
  const groupedNonFavorites = nonFavoriteChannels.reduce((acc, channel) => {
    const group = channel.group || 'Uncategorized';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(channel);
    return acc;
  }, {} as Record<string, Channel[]>);

  // Sort channels within each group by name
  Object.keys(groupedNonFavorites).forEach(group => {
    groupedNonFavorites[group].sort((a: Channel, b: Channel) => a.name.localeCompare(b.name));
  });

  // Get unique group names from non-favorites
  const allGroups = Object.keys(groupedNonFavorites).sort();

  // Check if there are any channels
  const hasChannels = channels.length > 0;

  const handleExport = () => {
    if (channels.length === 0) {
      return;
    }
    
    // Export the playlist
    const m3u8Content = generateM3U8(channels);
    const blob = new Blob([m3u8Content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "playlist.m3u8";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Show the share modal in export mode
    setIsShareModalOpen(true);
  };

  const handleDeleteAll = async () => {
    try {
      setIsDeleting(true);
      setPreventReload(true);
      
      // If user is authenticated and we're deleting all channels
      if (user?.uid && selectedChannels.length === 0) {
        await deletePlaylist(user.uid);
        dispatch(clearChannels());
        setMode('view'); // Exit edit mode after deletion
        setHasUnsavedChanges(false); // Reset unsaved changes state
      } else {
        // If we're deleting selected channels or user is not authenticated
        if (selectedChannels.length > 0) {
          // Remove only selected channels
          const remainingChannels = channels.filter(ch => !selectedChannels.includes(ch.id));
          dispatch(setChannels(remainingChannels));
          setHasUnsavedChanges(true); // Set unsaved changes to trigger save button animation
        } else {
          // Delete all channels locally
          dispatch(clearChannels());
          setHasUnsavedChanges(false);
        }
      }
      
      dispatch(setError({ 
        message: selectedChannels.length > 0 
          ? `${selectedChannels.length} channel${selectedChannels.length !== 1 ? 's' : ''} deleted successfully`
          : 'All channels have been deleted', 
        type: 'success' 
      }));
      setShowDeleteConfirm(false);
      setActiveTab('channels');
      dispatch(clearSelectedChannels());
    } catch (error) {
      logger.error('Error deleting playlist:', error);
      dispatch(setError({ message: 'Failed to delete channels', type: 'error' }));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditModeClick = () => {
    dispatch(clearSelectedChannels()); // Clear selections first
    if (mode === 'edit') {
      setMode('view');
      setEditingChannel(null); // Clear the editing channel state when exiting edit mode
      if (activeTab === 'add') {
        setActiveTab('channels'); // Switch back to channels tab if currently on add tab
      }
    } else {
      setMode('edit');
    }
  };

  const handleShareClick = () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }

    dispatch(clearSelectedChannels()); // Clear selections first
    if (mode === 'share') {
      setMode('view');
      setEditingChannel(null); // Clear editing channel state when exiting share mode
      if (activeTab === 'add') {
        setActiveTab('channels'); // Switch back to channels tab if currently on add tab
      }
    } else {
      setMode('share');
    }
  };

  const handleChannelClick = (channel: Channel) => {
    if (mode === 'share') {
      dispatch(toggleChannelSelection(channel.id));
    } else {
      // In all other modes (including edit mode), try to play the channel
      // Try to handle as HTTP stream first
      const wasHandledByExtension = handleHttpStream(channel.url);
      
      // If not handled by extension (i.e. HTTPS stream), play normally
      if (!wasHandledByExtension) {
        if (isHttpStream(channel.url)) {
          // If we have an HTTP stream but it wasn't handled by the extension, show an error
          dispatch(setError({ 
            message: getExtensionErrorMessage(), 
            type: 'error' 
          }));
        } else {
          // For HTTPS streams, play normally
          handleChannelSelect(channel);
        }
      }
    }
  };

  // Add new function to handle checkbox clicks in edit mode
  const handleCheckboxClick = (e: React.MouseEvent, channelId: string) => {
    e.stopPropagation(); // Prevent channel click (which would play the channel)
    dispatch(toggleChannelSelection(channelId));
  };

  // Handle sharing selected channels
  const handleShareSelected = async () => {
    try {
      setIsGeneratingShare(true);
      setShareError(undefined);
      setIsShareModalOpen(true);

      // Get channels to share (either selected or all)
      const channelsToShare = selectedChannels.length > 0
        ? channels.filter(ch => selectedChannels.includes(ch.id))
        : channels;

      // Create shared playlist
      const shareId = await shareService.createSharedPlaylist(channelsToShare, user?.uid);
      const url = shareService.generateShareUrl(shareId);
      setShareUrl(url);
    } catch (error) {
      logger.error('Error sharing playlist:', error);
      setShareError('Failed to generate share link. Please try again.');
    } finally {
      setIsGeneratingShare(false);
    }
  };

  const handleSavePlaylist = async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }

    try {
      setIsSaving(true);
      await autoSavePlaylist();
      setHasUnsavedChanges(false);
      dispatch(setError({ message: 'Playlist saved successfully!', type: 'success' }));
    } catch (error: any) {
      logger.error('Save playlist error:', error);
      let errorMessage = 'Failed to save playlist';
      
      if (error.code) {
        switch (error.code) {
          case 'permission-denied':
            errorMessage = 'Permission denied. Please try logging out and back in.';
            break;
          case 'unauthenticated':
            errorMessage = 'Authentication required. Please log in again.';
            break;
          case 'unavailable':
            errorMessage = 'Service temporarily unavailable. Please try again later.';
            break;
          default:
            errorMessage = `Error: ${error.message || 'Failed to save playlist'}`;
        }
      }
      
      dispatch(setError({ message: errorMessage, type: 'error' }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddChannel = () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    setActiveTab('add');
  };

  const handleUrlImport = async () => {
    if (!urlInput.trim()) {
      dispatch(setError({ message: t('errors.invalidUrl', 'Please enter a valid URL'), type: 'error' }));
      return;
    }

    try {
      const url = new URL(urlInput.trim());
      // Remove HTTPS-only check for playlist URL
      
      dispatch(setLoading(true));
      try {
        logger.debug('Fetching playlist from URL:', url.toString());
        const response = await fetch(url.toString(), {
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Accept': 'text/plain, application/x-mpegurl, application/vnd.apple.mpegurl',
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch playlist');
        }

        const content = await response.text();
        logger.debug('Parsing M3U8 content, content length:', content.length);
        const parsedChannels = parseM3U8(content);
        logger.debug('Parsed channels from URL:', parsedChannels.length);
        
        // Accept all channels instead of filtering by HTTPS
        const validChannels = parsedChannels.filter((channel: any) => {
          try {
            new URL(channel.url); // Just validate URL format
            return true;
          } catch {
            return false;
          }
        });
        
        const filteredCount = parsedChannels.length - validChannels.length;
        logger.debug('Valid channels:', { 
          total: parsedChannels.length, 
          valid: validChannels.length, 
          filtered: filteredCount,
          sampleUrl: validChannels.length > 0 ? validChannels[0].url : 'none'
        });
        
        if (validChannels.length === 0) {
          dispatch(setError({ message: t('errors.noValidChannels', 'No valid channels found in the playlist'), type: 'error' }));
          return;
        }

        logger.debug('Current channels in Redux state before update:', channels.length);
        
        // Create a copy of existing channels to ensure we have the right data for saving
        const existingChannels = [...channels];
        
        // First update the Redux store
        dispatch(appendChannels(validChannels));
        logger.debug('Dispatched appendChannels action, channels to add:', validChannels.length);

        // Auto-save for both authenticated and non-authenticated users
        setIsAutoSaving(true);
        try {
          logger.debug('Starting auto-save process');
          
          // Wait for state to update - increase delay to ensure state propagation
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Calculate what the new state should be (for logging)
          const expectedChannelCount = existingChannels.length + validChannels.length;
          logger.debug('Expected channel count after update:', expectedChannelCount);
          logger.debug('Actual channel count in Redux before save:', channels.length);
          
          // If Redux state hasn't updated yet, use a direct save approach
          if (channels.length < expectedChannelCount) {
            // For authenticated users: save to Firestore
            if (user?.uid) {
              logger.debug('Redux state not yet updated, using direct save with combined channels to Firestore');
              const combinedChannels = [...existingChannels, ...validChannels];
              await savePlaylist(user.uid, combinedChannels);
            } 
            // For non-authenticated users: save to local storage
            else {
              logger.debug('Redux state not yet updated, saving combined channels to local storage');
              const combinedChannels = [...existingChannels, ...validChannels];
              saveChannelsLocally(combinedChannels);
            }
          } else {
            // Otherwise use autoSave which handles both auth and non-auth users
            logger.debug('Using autoSave with updated Redux state');
            await autoSave();
          }
          
          logger.debug('Auto-save completed successfully');
          
          dispatch(setError({ 
            message: filteredCount > 0 
              ? t('playlist.importAndSaveSuccessWithInvalid', 'Successfully imported and saved {{count}} channels ({{filtered}} invalid channels were filtered out)', { count: validChannels.length, filtered: filteredCount })
              : t('playlist.importAndSaveSuccess', 'Successfully imported and saved {{count}} channels', { count: validChannels.length }),
            type: 'success' 
          }));
        } catch (error) {
          logger.error('Failed to auto-save playlist:', error);
          dispatch(setError({ 
            message: t('errors.autoSaveFailed', 'Channels imported but failed to save automatically. Please try saving manually.'),
            type: 'error' 
          }));
        } finally {
          setIsAutoSaving(false);
        }
        
        setUrlInput('');
        setActiveTab('channels');
      } catch (error) {
        logger.error('Error importing playlist from URL:', error);
        dispatch(setError({ message: t('errors.urlImportError', 'Failed to import playlist from URL'), type: 'error' }));
      } finally {
        dispatch(setLoading(false));
      }
    } catch (error) {
      dispatch(setError({ message: t('errors.invalidUrl', 'Please enter a valid URL'), type: 'error' }));
    }
  };

  // Modify the save button to show auto-saving state
  const renderSaveButton = () => (
    <button
      onClick={handleSavePlaylist}
      disabled={!hasChannels || !hasUnsavedChanges || isSaving || isAutoSaving}
      className={clsx(
        'relative inline-flex items-center justify-center p-2.5 rounded-lg transition-all duration-200 ease-in-out',
        !hasChannels
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800/50 dark:text-gray-600'
          : hasUnsavedChanges && !isSaving && !isAutoSaving
            ? user 
              ? 'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
              : 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 dark:bg-blue-500/20 dark:text-blue-400 dark:hover:bg-blue-500/30'
            : 'bg-blue-500/10 text-blue-600/50 hover:bg-blue-500/20 dark:bg-blue-500/20 dark:text-blue-400/50 dark:hover:bg-blue-500/30',
        hasUnsavedChanges && !isSaving && !isAutoSaving && user ? 'animate-pulse' : '',
        'hover:scale-105 active:scale-95 group'
      )}
      title={!hasChannels 
        ? t('playlist.addChannelsToSave') 
        : !hasUnsavedChanges 
          ? t('playlist.noChangesToSave') 
          : !user
            ? t('playlist.loginToSaveChanges')
            : isAutoSaving
              ? t('playlist.autoSaving')
              : t('playlist.saveChanges')}
    >
      {isSaving || isAutoSaving ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <Save className="w-5 h-5 transition-transform duration-200 group-hover:rotate-12" />
      )}
    </button>
  );

  // Add a helper function to check URL protocol
  const getUrlProtocol = (url: string): 'http' | 'https' => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'https:' ? 'https' : 'http';
    } catch {
      return 'http'; // Default to http for invalid URLs
    }
  };

  return (
    <div className="w-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 rounded-lg overflow-auto md:overflow-hidden relative min-h-[300px] max-h-[calc(100vh-12rem)] md:flex md:flex-col">
      {/* Header Section - Only sticky on desktop */}
      <div className="bg-white dark:bg-gray-800 md:sticky md:top-0 md:z-10 md:flex-none">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            className={clsx(
              'flex-1 p-4 text-sm font-medium',
              activeTab === 'channels'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            )}
            onClick={() => setActiveTab('channels')}
          >
            <div className="flex items-center justify-center gap-2">
              <Folder className="w-4 h-4" />
              {t('playlist.channels', 'Channels')}
            </div>
          </button>
          <button
            className={clsx(
              'flex-1 p-4 text-sm font-medium',
              activeTab === 'add'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            )}
            onClick={() => setActiveTab('add')}
          >
            <div className="flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" />
              {t('playlist.add', 'Add')}
            </div>
          </button>
        </div>

        {/* Error Messages */}
        {error && (
          <div className={clsx(
            'p-4 relative',
            notificationType === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500' 
              : 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500'
          )}>
            <p className={clsx(
              'text-sm pr-8',
              notificationType === 'success'
                ? 'text-green-700 dark:text-green-400'
                : 'text-red-700 dark:text-red-400'
            )}>
              {error}
            </p>
            <button
              onClick={handleDismissError}
              className={clsx(
                'absolute top-4 right-4',
                notificationType === 'success'
                  ? 'text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'
                  : 'text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
              )}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {activeTab === 'channels' && (
          <>
            {/* Action Buttons */}
            <div className="flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-700">
              <div className="w-full grid grid-cols-4 gap-2">
                <button
                  onClick={handleEditModeClick}
                  disabled={!hasChannels}
                  className={clsx(
                    'relative inline-flex items-center justify-center p-2.5 rounded-lg transition-all duration-200 ease-in-out',
                    !hasChannels
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800/50 dark:text-gray-600'
                      : mode === 'edit'
                        ? 'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
                        : 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 dark:bg-blue-500/20 dark:text-blue-400 dark:hover:bg-blue-500/30'
                  )}
                  title={!hasChannels ? t('playlist.addChannelsToEdit') : t('playlist.editPlaylist')}
                >
                  <Edit className="w-5 h-5 transition-transform duration-200 group-hover:rotate-12" />
                </button>
                
                <button
                  onClick={handleExport}
                  disabled={!hasChannels}
                  className={clsx(
                    'relative inline-flex items-center justify-center p-2.5 rounded-lg transition-all duration-200 ease-in-out',
                    !hasChannels
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800/50 dark:text-gray-600'
                      : 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 dark:bg-blue-500/20 dark:text-blue-400 dark:hover:bg-blue-500/30'
                  )}
                  title={!hasChannels ? t('playlist.addChannelsToExport') : t('playlist.exportPlaylist')}
                >
                  <Download className="w-5 h-5 transition-transform duration-200 group-hover:rotate-12" />
                </button>

                {renderSaveButton()}

                <ShareButton
                  onShare={handleShareClick}
                  onAuthRequired={() => navigate('/sign-in')}
                  disabled={!hasChannels}
                  isActive={mode === 'share'}
                />
              </div>
            </div>

            {/* Share/Delete All Button - Show appropriate button based on mode */}
            {(mode === 'edit' || mode === 'share') && hasChannels && (
              <div className="px-4 pt-2 pb-4">
                {mode === 'edit' ? (
                  showDeleteConfirm ? (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <p className="text-sm text-red-700 dark:text-red-400 mb-4">
                        {selectedChannels.length > 0 
                          ? t('playlist.deleteSelectedConfirmation', { count: selectedChannels.length })
                          : t('playlist.deleteConfirmation')}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleDeleteAll}
                          disabled={isDeleting}
                          className="flex-1 flex items-center justify-center gap-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {isDeleting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {t('playlist.deleting')}
                            </>
                          ) : (
                            selectedChannels.length > 0 
                              ? t('playlist.yesDeleteSelected')
                              : t('playlist.yesDeleteAll')
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            dispatch(clearSelectedChannels());
                          }}
                          disabled={isDeleting}
                          className="flex-1 p-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full flex items-center justify-center gap-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      {selectedChannels.length > 0 
                        ? t('playlist.deleteSelected', { count: selectedChannels.length })
                        : t('playlist.deleteAll')}
                    </button>
                  )
                ) : (
                  <button
                    onClick={handleShareSelected}
                    disabled={isGeneratingShare}
                    className="w-full flex items-center justify-center gap-2 p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isGeneratingShare ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('playlist.generatingShareLink')}
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4" />
                        {selectedChannels.length > 0 
                          ? t('playlist.shareSelected', { count: selectedChannels.length })
                          : t('playlist.shareAll')}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Search Input - Only show when there are channels */}
            {hasChannels && (
              <div className="px-4 pt-2 pb-4 space-y-2">
                {/* Extension notification - show above search */}
                <ExtensionNotification />
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search channels..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-primary focus:border-primary"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Scrollable Content Section */}
      <div className="md:overflow-y-auto md:flex-1 md:min-h-0">
        {activeTab === 'channels' ? (
          <div className="p-4 w-full bg-white dark:bg-gray-800">
            {!hasChannels ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <Folder className="w-12 h-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {t('playlist.noChannelsYet', 'No channels yet')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {t('playlist.addFirstChannel', 'Add your first channel using the Add tab')}
                </p>
                <button
                  onClick={() => setActiveTab('add')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('playlist.addChannel', 'Add Channel')}
                </button>
              </div>
            ) : (
              <>
                {/* Render favorites first */}
                {favoriteChannels.length > 0 && (
                  <div key="favorites" className="mb-4">
                    <div className="px-4 py-2 text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50">
                      {t('playlist.favorites', 'Favorites')}
                    </div>
                    <div className="space-y-1">
                      {favoriteChannels.map((channel) => (
                        <div
                          key={channel.url}
                          onClick={() => handleChannelClick(channel)}
                          className={clsx(
                            'w-full flex items-center p-2 cursor-pointer',
                            currentChannel?.id === channel.id
                              ? 'bg-blue-50 dark:bg-blue-900/50'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50',
                            selectedChannels.includes(channel.id) ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''
                          )}
                        >
                          {/* Add checkbox for edit mode */}
                          {mode === 'edit' && (
                            <div 
                              onClick={(e) => handleCheckboxClick(e, channel.id)}
                              className="mr-2 cursor-pointer"
                              title={t('playlist.selectForDeletion', 'Select for deletion')}
                            >
                              {selectedChannels.includes(channel.id) ? (
                                <CheckSquare className="w-5 h-5 text-blue-500" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          )}
                          
                          {channel.logo ? (
                            <img
                              src={channel.logo}
                              alt={t('altText.channelLogo', { channelName: channel.name })}
                              className="w-8 h-8 rounded mr-3"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded mr-3 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                              <Tv className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 flex items-center">
                            <span className="text-left text-sm font-medium text-gray-900 dark:text-white">
                              {channel.name}
                            </span>
                            {getUrlProtocol(channel.url) === 'http' && (
                              <div 
                                className="ml-2 text-blue-400 dark:text-blue-300" 
                                title={t('playlist.httpStreamTooltip', 'This HTTP stream will open in the external player extension. Click to play.')}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {mode === 'edit' ? (
                              <>
                                <button
                                  onClick={(e) => handleEditChannel(e, channel)}
                                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                  title={t('playlist.editChannel')}
                                >
                                  <Edit className="w-4 h-4 text-blue-500" />
                                </button>
                              </>
                            ) : mode === 'share' ? (
                              <div className={clsx(
                                'w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors',
                                selectedChannels.includes(channel.id)
                                  ? 'border-blue-500 bg-blue-500'
                                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-500'
                              )}>
                                {selectedChannels.includes(channel.id) && (
                                  <Check className="w-4 h-4 text-white" />
                                )}
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={(e) => handleToggleFavorite(e, channel.id)}
                                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                  title={favorites.includes(channel.id) ? "Remove from favorites" : "Add to favorites"}
                                >
                                  <Pin className={clsx(
                                    'w-4 h-4',
                                    favorites.includes(channel.id) ? 'text-blue-400 dark:text-blue-300 fill-blue-400 dark:fill-blue-300' : 'text-gray-400'
                                  )} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show no results message if searching with no matches */}
                {searchQuery && favoriteChannels.length === 0 && Object.keys(groupedNonFavorites).length === 0 && (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    {t('playlist.noChannelsFound', 'No channels found matching "{searchQuery}"')}
                  </div>
                )}

                {/* Render non-favorites grouped */}
                {allGroups.map((group) => (
                  groupedNonFavorites[group]?.length > 0 && (
                    <div key={group} className="mb-4">
                      <div className="px-4 py-2 text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50">
                        {group}
                      </div>
                      <div className="space-y-1">
                        {groupedNonFavorites[group].map((channel: Channel) => (
                          <div
                            key={channel.url}
                            onClick={() => handleChannelClick(channel)}
                            className={clsx(
                              'w-full flex items-center p-2 cursor-pointer',
                              currentChannel?.id === channel.id
                                ? 'bg-blue-50 dark:bg-blue-900/50'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50',
                              selectedChannels.includes(channel.id) ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''
                            )}
                          >
                            {/* Add checkbox for edit mode */}
                            {mode === 'edit' && (
                              <div 
                                onClick={(e) => handleCheckboxClick(e, channel.id)}
                                className="mr-2 cursor-pointer"
                                title={t('playlist.selectForDeletion', 'Select for deletion')}
                              >
                                {selectedChannels.includes(channel.id) ? (
                                  <CheckSquare className="w-5 h-5 text-blue-500" />
                                ) : (
                                  <Square className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                            )}
                            
                            {channel.logo ? (
                              <img
                                src={channel.logo}
                                alt={t('altText.channelLogo', { channelName: channel.name })}
                                className="w-8 h-8 rounded mr-3"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded mr-3 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                <Tv className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1 flex items-center">
                              <span className="text-left text-sm font-medium text-gray-900 dark:text-white">
                                {channel.name}
                              </span>
                              {getUrlProtocol(channel.url) === 'http' && (
                                <div 
                                  className="ml-2 text-blue-400 dark:text-blue-300" 
                                  title={t('playlist.httpStreamTooltip', 'This HTTP stream will open in the external player extension. Click to play.')}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {mode === 'edit' ? (
                                <>
                                  <button
                                    onClick={(e) => handleEditChannel(e, channel)}
                                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                    title={t('playlist.editChannel')}
                                  >
                                    <Edit className="w-4 h-4 text-blue-500" />
                                  </button>
                                </>
                              ) : mode === 'share' ? (
                                  <div className={clsx(
                                    'w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors',
                                    selectedChannels.includes(channel.id)
                                      ? 'border-blue-500 bg-blue-500'
                                      : 'border-gray-300 dark:border-gray-600 hover:border-blue-500'
                                  )}>
                                    {selectedChannels.includes(channel.id) && (
                                      <Check className="w-4 h-4 text-white" />
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      onClick={(e) => handleToggleFavorite(e, channel.id)}
                                      className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                      title={favorites.includes(channel.id) ? "Remove from favorites" : "Add to favorites"}
                                    >
                                      <Pin className={clsx(
                                        'w-4 h-4',
                                        favorites.includes(channel.id) ? 'text-blue-400 dark:text-blue-300 fill-blue-400 dark:fill-blue-300' : 'text-gray-400'
                                      )} />
                                    </button>
                                  </>
                                )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4 p-4">
            <PlaylistImport onImportSuccess={handleImportSuccess} />
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  {t('playlist.orImportUrl', 'or import from URL')}
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={t('playlist.urlPlaceholder', 'https://example.com/playlist.m3u8')}
                className="w-full px-4 py-2 border rounded-lg text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-primary focus:border-primary"
              />
              <button
                onClick={handleUrlImport}
                className="w-full py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('playlist.import')}
              </button>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  {editingChannel ? t('playlist.editStream', 'edit stream') : t('playlist.orAddSingleStream', 'or add single stream')}
                </span>
              </div>
            </div>
            
            <LinkInput 
              editingChannel={editingChannel} 
              onEditComplete={() => {
                setEditingChannel(null);
                setActiveTab('channels');
              }} 
            />
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('playlist.loadingPlaylist', 'Loading your playlist...')}
            </p>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => {
          setIsShareModalOpen(false);
          setShareUrl(undefined);
          setShareError(undefined);
          setMode('view');
          dispatch(clearSelectedChannels());
          setEditingChannel(null);
          if (activeTab === 'add') {
            setActiveTab('channels');
          }
        }}
        channels={selectedChannels.length > 0
          ? channels.filter(ch => selectedChannels.includes(ch.id))
          : channels}
        shareUrl={shareUrl}
        isGenerating={isGeneratingShare}
        error={shareError}
        isExport={mode !== 'share'}
      />

      {/* Extension Not Found Notification */}
      {error && error.includes('extension') && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 relative">
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {error}
            <a 
              href={getExtensionStoreUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 underline hover:text-amber-800 dark:hover:text-amber-300"
            >
              {t('extensions.installBrowserExtension')}
            </a>
          </p>
          <button
            onClick={handleDismissError}
            className="absolute top-4 right-4 text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
